import { WSLoader } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import {
  InternalAccountType,
  PaymentMethodRef,
  PaymentMethodType
} from "@wingspanhq/wingspan-api-sdk";
import {
  queriesExternalBankAccounts,
  queriesInternalAccounts,
  queriesPayers
} from "@query";
import { selectorExternalBankAccountFundingReady } from "@selectors/selectorExternalBankAccountFundingReady";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorMethodValueFromPaymentMethodRef } from "../../../../shared/selectors/selectorMethodValueFromPaymentMethodRef";
import { ManageMethods } from "../ManageMethods";
import { MethodValue } from "../ManageMethods/types";

// The inverse of selectorMethodValueFromPaymentMethodRef. `CreditCard` has no
// v3 ref — legacy Stripe cards lived on the retired client profile — so it
// returns undefined rather than inventing one.
function toPaymentMethodRef(value: MethodValue): PaymentMethodRef | undefined {
  switch (value.type) {
    case "Account":
      return { type: PaymentMethodType.EXTERNAL_BANK_ACCOUNT, id: value.id };

    case "InternalAccount":
      return { type: PaymentMethodType.INTERNAL_ACCOUNT, id: value.id };

    case "PaymentCard":
      return { type: PaymentMethodType.PAYMENT_CARD, id: value.id };

    default:
      return undefined;
  }
}

type Props = {
  onNext: () => void;
  onAddNew: () => void;
};

export const ManageFundingMethod: React.FC<Props> = ({ onNext, onAddNew }) => {
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery();
  const queryPayerSettings = queriesPayers.getCurrentPayerSettings.useQuery();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery({
      filter: { type: { eq: InternalAccountType.BANKING } }
    });

  const externalBankAccounts = useMemo(
    () =>
      (queryExternalBankAccounts.data?.data || []).filter(
        selectorExternalBankAccountFundingReady
      ),
    [queryExternalBankAccounts.data]
  );

  const internalAccounts = useMemo(
    () =>
      (queryInternalAccounts.data?.data || []).filter(
        selectorActiveBankingAccount
      ),
    [queryInternalAccounts.data]
  );

  const initialValue = useMemo(
    () =>
      selectorMethodValueFromPaymentMethodRef(
        queryPayerSettings.data?.payrollSettings?.defaultFundingSource
      ),
    [queryPayerSettings.data?.payrollSettings?.defaultFundingSource]
  );

  const [updatePayerSettings, updatePayerSettingsMeta] =
    queriesPayers.updateCurrentPayerSettings.useMutation({ onSuccess: onNext });

  if (
    queryExternalBankAccounts.isLoading ||
    queryPayerSettings.isLoading ||
    queryInternalAccounts.isLoading
  ) {
    return <WSLoader.Spinner />;
  }

  return (
    <ManageMethods
      data={{
        accounts: externalBankAccounts,
        internalAccounts
      }}
      header={{
        label: {
          text: "Manage payroll funding method",
          helperText:
            "Set an existing payroll funding method as default or add a new payroll funding method"
        }
      }}
      onAddNew={onAddNew}
      addNewLabel="Add new payroll funding method"
      initialValue={initialValue}
      onSubmit={value => {
        const defaultFundingSource = value && toPaymentMethodRef(value);

        if (!defaultFundingSource) {
          return;
        }

        updatePayerSettings({
          currentPayerSettingsUpdate: {
            payrollSettings: { defaultFundingSource }
          }
        });
      }}
      error={updatePayerSettingsMeta.error}
      isLoading={updatePayerSettingsMeta.isLoading}
    />
  );
};
